import CollectionPlaceholder from '@design-system/collection-placeholder';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProjectPath } from '@constant/gridgio/route-path';
import styled from 'styled-components';
import SearchInput from '@design-system/input/search-input';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import MapThumbnail from '@component/common/map-thumbnail';
import IconBlock from '@component/common/block/icon-block';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import TextButton from '@design-system/text-button';
import Skeleton from '@design-system/skeleton';
import { useProjectInfiniteQuery, useBookmarkProjectInfiniteQuery, useModifyProjectMutation } from '@hook/gridgio/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';

const MapThumbnailContainer = styled.div`
  width: 160px;
  height: 100px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
`;

const PinnedProjectItemsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
`;
const PinnedProjectItemContainer = styled.div`
  display: flex;
  width: 360px;
  min-height: 100px;
  align-items: center;
  gap: ${size.MARGIN_1};
`;
const PinnedProjectInfoContainer = styled.div`
  display: flex;
  align-self: stretch;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

const PinnedProjectMetaContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const PinnedProjectCardContainer = styled.div`
  position: relative;
  margin: 10px 5px 0 0;
`;

const PinnedProjectCloseButtonContainer = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
  cursor: pointer;
  z-index: 1;
`;

const ProjectControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${size.MARGIN_2};
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProjectListControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ProjectItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;

const ProjectItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 80px;
  align-items: center;
`;

const ProjectMetaContainer = styled.div`
  display: flex;
  align-self: stretch;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
`;

const ProjectActionControlContainer = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
`;

const ProjectActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const ProjectTitleDescriptionContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const ProjectLocationDateContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const Home: React.FC = () => {
  const dispatch = useTypedDispatch();
  const handleMapPreview = (latitude?: number, longitude?: number) => {
    if (latitude === undefined || longitude === undefined) return;
    dispatch(modalActions.showDialogModal({ type: 'map-preview', data: { latitude, longitude } }));
  };
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bookmarkScrollContainerRef = useRef<HTMLDivElement>(null);
  const projectQuery = useProjectInfiniteQuery(12);
  const projects = useMemo(
    () => projectQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [projectQuery.data],
  );
  const bookmarkQuery = useBookmarkProjectInfiniteQuery(12);
  const pinnedProjects = useMemo(
    () => bookmarkQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [bookmarkQuery.data],
  );
  const bookmarkMutation = useModifyProjectMutation();
  const handleBookmark = (projectKey: string, isBookmark: boolean) => {
    if (bookmarkMutation.isPending) return;
    bookmarkMutation.mutate({ projectKey, isBookmark: !isBookmark });
  };
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: (projectQuery.hasNextPage ?? false) && !projectQuery.isFetchNextPageError,
    isFetchingNextPage: projectQuery.isFetchingNextPage,
    fetchNextPage: projectQuery.fetchNextPage,
    root: scrollContainerRef,
  });
  const { sentinelRef: bookmarkSentinelRef } = useInfiniteScroll({
    hasNextPage: (bookmarkQuery.hasNextPage ?? false) && !bookmarkQuery.isFetchNextPageError,
    isFetchingNextPage: bookmarkQuery.isFetchingNextPage,
    fetchNextPage: bookmarkQuery.fetchNextPage,
    root: bookmarkScrollContainerRef,
  });
  const handlePlannedClick = () => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }));
  // TODO: 대표 현장 사진을 지원하면 지도보다 우선 표시한다.
  const PinnedProjectItems = pinnedProjects.map((project) => (
    <PinnedProjectCardContainer key={project.key}>
      <Canvas
        theme={ETheme.WHITE}
        padding={size.MARGIN_1}
        border
        shadow>
        <PinnedProjectItemContainer>
          <MapThumbnailContainer>
            <MapThumbnail
            latitude={project.coordinate?.latitude}
            longitude={project.coordinate?.longitude}
            handleExpandClick={() => handleMapPreview(project.coordinate?.latitude, project.coordinate?.longitude)}
          />
          </MapThumbnailContainer>
          <PinnedProjectInfoContainer>
            <PinnedProjectMetaContainer>
              <TextBlock
                text={project.name}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
                }}
              />
              <IconTextBlock
                icon='map-pin'
                text={[project.place, project.address].filter(Boolean).join(' · ') || '주소 미등록'}
                design={{
                  box: { width: '100%', alignX: 'left', gap: '2px' },
                  icon: { size: 'tiny', color: color.GRAY_1 },
                  text: { size: 'small', color: color.GRAY_1, oneline: true },
                }}
              />
            </PinnedProjectMetaContainer>
            <TextButton
              text='프로젝트 열기'
              theme={ETheme.CYAN}
              tone='normal'
              kind='outlined'
              size='small'
              shape='round'
              width='100%'
              // height='32px'
              handleClick={() => navigate(createProjectPath('project', project.key))}
            />
          </PinnedProjectInfoContainer>
        </PinnedProjectItemContainer>
      </Canvas>
      <PinnedProjectCloseButtonContainer
        onClick={() => dispatch(modalActions.showDialogModal({
          type: 'project-bookmark-remove',
          data: { projectKey: project.key },
        }))}>
        <IconBlock icon='close' design={{ icon: { size: 'tiny', color: color.GRAY_1 } }} />
      </PinnedProjectCloseButtonContainer>
    </PinnedProjectCardContainer>
  ));
  const ProjectItems = projects.map((project) => (
    <Canvas
      key={project.key}
      theme={ETheme.WHITE}
      width='100%'
      padding={size.MARGIN_1}
      border
      shadow>
      <ProjectItemContainer>
        <MapThumbnailContainer>
          <MapThumbnail
            latitude={project.coordinate?.latitude}
            longitude={project.coordinate?.longitude}
            handleExpandClick={() => handleMapPreview(project.coordinate?.latitude, project.coordinate?.longitude)}
          />
        </MapThumbnailContainer>
        <Gap width={size.MARGIN_2} />
        <ProjectMetaContainer>
          <ProjectTitleDescriptionContainer>
            <TextBlock
              text={project.name}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
              }}
            />
            {project.description && (
              <TextBlock
                text={project.description}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { size: 'medium', color: color.BLACK_4, oneline: true },
                }}
              />
            )}
          </ProjectTitleDescriptionContainer>
          <ProjectLocationDateContainer>
            <IconTextBlock
              icon='map-pin'
              text={[project.place, project.address].filter(Boolean).join(' · ') || '주소 미등록'}
              design={{
                box: { width: '100%', alignX: 'left', gap: '2px' },
                icon: { size: 'tiny', color: color.GRAY_1 },
                text: { size: 'small', color: color.GRAY_1, oneline: true },
              }}
            />
            <IconTextBlock
              icon='clock'
              text={new Date(project.updatedAt).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })}
              design={{
                box: { gap: '2px' },
                icon: { size: 'tiny', color: color.GRAY_1 },
                text: { size: 'small', color: color.GRAY_1 },
              }}
            />
          </ProjectLocationDateContainer>
        </ProjectMetaContainer>
        <ProjectActionControlContainer>
          <ProjectActionContainer>
            <IconBlock
              icon='pin'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1, checked: Boolean(project.isBookmark), checkedColor: color.CYAN_2 },
              }}
              click={{ handler: () => handleBookmark(project.key, Boolean(project.isBookmark)) }}
            />
            <IconBlock
              icon='edit'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () => dispatch(modalActions.showDialogModal({
                  type: 'project-edit',
                  data: project,
                })),
              }}
            />
            <IconBlock
              icon='trash'
              design={{
                box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
                icon: { size: 'small', color: color.GRAY_1 },
              }}
              click={{
                handler: () => dispatch(modalActions.showDialogModal({
                  type: 'project-remove',
                  data: { projectKey: project.key },
                })),
              }}
            />
          </ProjectActionContainer>
          <TextButton
            text='프로젝트 열기'
            theme={ETheme.CYAN}
            tone='normal'
            kind='outlined'
            size='small'
            shape='round'
            width='100%'
            // height='32px'
            handleClick={() => navigate(createProjectPath('project', project.key))}
          />
        </ProjectActionControlContainer>
      </ProjectItemContainer>
    </Canvas>
  ));
  return (
    <Wrapper>
      <TextBlock
        text='프로젝트를 만들고, 현장별 도면 작업을 관리하세요.'
        design={{ box: { alignX: 'left' }, text: { size: 'large', color: color.BLACK_5, bold: true } }}
      />
      <Gap height={size.MARGIN_1} />
      <IconTextBlock
        icon='pin'
        text='고정 프로젝트'
        design={{
          box: { alignX: 'left', gap: '5px' },
          icon: { size: 'small', color: color.BLACK_2 },
          text: { size: 'medium', color: color.BLACK_2, bold: true },
        }}
      />
      <Gap height={size.MARGIN_1} />
      <Scroll direction='x' containerRef={bookmarkScrollContainerRef}>
        <PinnedProjectItemsContainer>
          {bookmarkQuery.isLoading ? (
            <Skeleton
              tone='normal'
              width={`calc(360px + ${size.MARGIN_1} * 2)`}
              height={`calc(100px + ${size.MARGIN_1} * 2)`}
              borderRadius={size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS}
            />
          ) : pinnedProjects.length > 0 ? (
            PinnedProjectItems
          ) : (
            <Canvas theme={ETheme.WHITE} padding={size.MARGIN_1} border shadow>
              <PinnedProjectItemContainer>
                <TextBlock
                  text='고정된 프로젝트가 없어요.'
                  design={{ box: { width: '100%', alignX: 'center' }, text: { size: 'medium', color: color.GRAY_1 } }}
                />
              </PinnedProjectItemContainer>
            </Canvas>
          )}
          <div ref={bookmarkSentinelRef} style={{ width: '1px', height: '1px', flexShrink: 0 }} />
        </PinnedProjectItemsContainer>
      </Scroll>
      <Gap height={size.MARGIN_2} />
      <ProjectControlContainer>
        <SearchContainer>
          <SearchInput
            value=''
            placeholder='프로젝트명 또는 주소로 검색'
            theme={ETheme.CYAN}
            width='100%'
            readOnly
            handleClick={() => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }))}
            handleSearch={() => dispatch(modalActions.showDialogModal({ type: 'feature-planned' }))}
          />
        </SearchContainer>
        <TextButton
          text='새 프로젝트'
          icon='map-pin-plus'
          arrangement='icon-text'
          theme={ETheme.CYAN}
          kind='filled'
          size='medium'
          width='120px'
          shape='pill'
          handleClick={() => dispatch(modalActions.showDialogModal({ type: 'project-create' }))}
        />
      </ProjectControlContainer>
      <Gap height={size.MARGIN_2} />
      <ProjectListControlContainer>
        <Chip
          isLoading={projectQuery.isLoading}
          text={`전체 프로젝트 ${projectQuery.data?.pages[0]?.totalCount ?? projects.length}개`}
          size='small'
          theme={ETheme.CYAN}
          kind='outlined'
          shape='round'
          usage='static'
        />
        <IconBlock
          icon='sort'
          design={{
            box: { width: '35px', height: '35px', event: { cursor: 'pointer' } },
            icon: { size: 'small', color: color.CYAN_2 },
          }}
          click={{ handler: handlePlannedClick }}
        />
      </ProjectListControlContainer>
      <Gap height={size.MARGIN_1} />
      <ScrollContainer width='100%'>
        <Scroll direction='y' height='100%' containerRef={scrollContainerRef}>
          {projectQuery.isLoading ? (
            <ProjectItemsContainer>
              <Skeleton tone='normal' height='120px' />
              <Skeleton tone='normal' height='120px' />
              <Skeleton tone='normal' height='120px' />
            </ProjectItemsContainer>
          ) : projects.length > 0 ? (
            <>
              <ProjectItemsContainer>{ProjectItems}</ProjectItemsContainer>
              <div ref={sentinelRef} style={{ width: '100%', height: '1px', flexShrink: 0 }} />
              {projectQuery.isFetchingNextPage && <Skeleton tone='normal' height='120px' />}
            </>
          ) : (
            <CollectionPlaceholder
              theme={ETheme.CYAN}
              width='100%'
              height='100%'
              icon='building'
              title='첫 프로젝트를 시작해보세요'
              description='현장 도면과 작업 기록을 한곳에서 관리하세요.'
              buttonText='프로젝트 만들기'
              handleButtonClick={() => dispatch(modalActions.showDialogModal({ type: 'project-create' }))}
            />
          )}
        </Scroll>
      </ScrollContainer>
    </Wrapper>
  );
};
export default Home;
